<template>
    <div>
        <page-header
            :text="$t('navigation.systems.parameters')"
            :button="{ text: $t('buttons.reload'), emitEvent: 'reloadParameters', ability: true }"
            :icon="'action-sync'"
            @reloadParameters="reload"
            :searchField="{
                placeholder: $t('systems.search'),
                inputEmitEvent: 'search',
                nameTranslation: 'system',
                savedSearchName: 'search-system-overview',
                simpleSearch: true,
            }"
            @search="searchHandler"
        />

        <page-subnavigation :items="allSystemLinks" class="slab-zero--bottom" />

        <div class="table-wrapper">
            <table class="table table--striped-dynamic table--striped-dynamic--unbordered slab-mobile--large--bottom" v-if="activeSystem">
                <thead class="table__head table__head--dark table__head--prominent table__head--sticky">
                    <tr>
                        <th class="table__data table__data--label"></th>
                        <th class="table__data table__data--parameter" v-for="boiler in boilerTab.columns" :key="boiler.index">
                            {{ boiler.name }}
                        </th>
                        <th></th>
                    </tr>
                </thead>

                <tbody v-if="structure && structure.length">
                    <template v-for="(row, row_index) in boilerParameters">
                        <tr v-if="compareChildren(row) && (searchInput === '' || row.filtered === false)" :key="row_index">
                            <td v-html="highlightQuery($t(row[Object.keys(row)[0]].translation || 'oids.' + row[Object.keys(row)[0]].oid))"></td>

                            <td v-for="column in boilerTab.columns" :key="column.index">
                                <lazy-parameter
                                    v-if="row[column.columnId]"
                                    :id="activeSystem.id"
                                    :oid="row[column.columnId].oid"
                                    :fctId="column.fctId"
                                    :node_id="column.nodeId"
                                    :active="true"
                                    :uneditable="true"
                                    :condition="row[column.columnId].condition"
                                    :endpoint="row[column.columnId].endpoint"
                                    :type="row[column.columnId].type"
                                    :enum="row[column.columnId].enum"
                                    :mask="row[column.columnId].mask"
                                    :bit="row[column.columnId].bit"
                                    :reset="row[column.columnId].reset"
                                    :omit="row[column.columnId].omit"
                                    :restart="row[column.columnId].restart ? row[column.columnId].restart : false"
                                    @newChild="registerNewChild($event, row)"
                                    @noChildFound="registerMissingChild($event, row)"
                                ></lazy-parameter>

                                <span v-if="!row[column.columnId]">n/a</span>
                            </td>
                            <td class="table__data table__data--no-border-left"></td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div class="tabs slab-mobile--large--bottom" v-if="activeSystem">
            <header class="tabs__header">
                <swiper-container init="false" ref="headerSlider">
                    <swiper-slide v-for="tab in tabs" :key="tab.name">
                        <div class="tabs__header__item" :class="{ active: tab.name === activeTab }" @click="switchTab(tab.name)">
                            {{ $t(`systems.parameters.tabs.${tab.name}`) }}
                            <span v-if="searchInput.length > 0" class="count">&nbsp;({{ parameterCount[tab.name].count }})</span>
                        </div>
                    </swiper-slide>
                </swiper-container>
            </header>
            <div class="tabs__subheader" v-for="tab in tabs" :key="tab.name" v-show="tab.name === activeTab">
                <swiper-container init="false" ref="subheaderSlider">
                    <swiper-slide v-for="layer in tab.layers" :key="layer.name">
                        <span
                            :key="layer.name"
                            class="tabs__subheader__item"
                            :class="{ active: isActiveLayer(tab.name, layer.name) }"
                            @click="switchLayer(tab.name, layer.name)"
                            v-if="tabVisible(layer.name, tab)"
                        >
                            {{ $t(`systems.parameters.layers.${layer.name}`) }}
                            <span v-if="searchInput.length > 0" class="count">&nbsp;({{ parameterCount[tab.name][layer.name] }})</span>
                        </span>
                    </swiper-slide>
                </swiper-container>
            </div>
            <div v-for="tab in tabs" :key="tab.name + '-content'" class="tabs__body">
                <template v-for="layer in tab.layers">
                    <div :key="layer.name" v-if="isActiveLayer(tab.name, layer.name)" class="tabs__content">
                        <div class="table-wrapper">
                            <table class="table table--striped-dynamic table--striped-dynamic--unbordered" v-if="structure && structure.length">
                                <thead class="table__head table__head--dark table__head--sticky">
                                    <tr>
                                        <th class="table__data table__data--label"></th>
                                        <th class="table__data table__data--parameter" v-for="(column, index) in tab.columns" :key="index">
                                            <div editable-module-name="{column: column, id: id, node_id: column.nodeId, function_id: column.fctId}"></div>
                                            <editable-module-name :column="column" :id="activeSystem.id" :node_id="column.nodeId" :function_id="column.fctId"></editable-module-name>
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(row, row_index) in layer.rows">
                                        <tr :key="row_index" v-if="compareChildren(row) && checkAccordionChildren(row) && (searchInput === '' || row.filtered === false)">
                                            <!-- accordion level 0 -->
                                            <td v-if="row.group_name" class="accordion accordion--table accordion--level-0" colspan="42" :class="{ open: row.accordionOpen || searchInput.length > 0 }">
                                                <div class="accordion__head" @click="toggleAccordionRow(row)">
                                                    <i class="icon icon--common-caret accordion__caret"></i>
                                                    <span
                                                        v-html="
                                                            highlightQuery($te('emStrIds.' + row.group_name) ? $t('emStrIds.' + row.group_name) : $t('systems.parameters.groups.' + row.group_name))
                                                        "
                                                    ></span>
                                                </div>
                                                <slide-down-up-transition>
                                                    <div class="accordion__content" v-if="row.accordionOpen || searchInput.length > 0">
                                                        <div class="table-wrapper">
                                                            <table class="table accordion__content__table">
                                                                <tbody>
                                                                    <template v-for="(groupParameter, index) in row.parameters">
                                                                        <tr :key="index" v-if="compareChildren(groupParameter) && (searchInput === '' || groupParameter.filtered === false)">
                                                                            <!-- accordion level 1 -->
                                                                            <td
                                                                                v-if="groupParameter.group_name"
                                                                                class="accordion accordion--table accordion--level-1"
                                                                                colspan="42"
                                                                                :class="{
                                                                                    open: groupParameter.accordionOpen || searchInput.length > 0,
                                                                                }"
                                                                            >
                                                                                <div class="accordion__head" @click="toggleAccordionRow(groupParameter)">
                                                                                    <i class="icon icon--common-caret accordion__caret"></i>

                                                                                    <span
                                                                                        v-html="
                                                                                            highlightQuery(
                                                                                                $te('emStrIds.' + groupParameter.group_name)
                                                                                                    ? $t('emStrIds.' + groupParameter.group_name)
                                                                                                    : $t('systems.parameters.groups.' + groupParameter.group_name)
                                                                                            )
                                                                                        "
                                                                                    ></span>
                                                                                </div>
                                                                                <slide-down-up-transition>
                                                                                    <div class="accordion__content" v-if="groupParameter.accordionOpen || searchInput.length > 0">
                                                                                        <table class="table accordion__content__table">
                                                                                            <tbody>
                                                                                                <template v-for="(groupParameter2, index2) in groupParameter.parameters">
                                                                                                    <tr
                                                                                                        :key="index2"
                                                                                                        v-if="
                                                                                                            compareChildren(groupParameter2) &&
                                                                                                                (searchInput === '' || groupParameter2.filtered === false)
                                                                                                        "
                                                                                                    >
                                                                                                        <td
                                                                                                            v-if="!groupParameter2.group_name"
                                                                                                            class="table__data table__data--label accordion__content__table__label"
                                                                                                        >
                                                                                                            <span
                                                                                                                v-if="layer.name == 'oem'"
                                                                                                                v-html="
                                                                                                                    highlightQuery(
                                                                                                                        $t('oids_oem.' + groupParameter2[Object.keys(groupParameter2)[0]].oid)
                                                                                                                    )
                                                                                                                "
                                                                                                            ></span>
                                                                                                            <span
                                                                                                                v-else
                                                                                                                v-html="
                                                                                                                    highlightQuery(
                                                                                                                        $t(
                                                                                                                            groupParameter2[Object.keys(groupParameter2)[0]].translation ||
                                                                                                                                'oids.' + groupParameter2[Object.keys(groupParameter2)[0]].oid
                                                                                                                        )
                                                                                                                    )
                                                                                                                "
                                                                                                            ></span>
                                                                                                        </td>

                                                                                                        <template v-for="(column, index) in tab.columns">
                                                                                                            <td
                                                                                                                v-if="!groupParameter2.group_name"
                                                                                                                class="table__data table__data--parameter"
                                                                                                                :key="index"
                                                                                                            >
                                                                                                                <lazy-parameter
                                                                                                                    v-if="groupParameter2[column.columnId]"
                                                                                                                    :id="activeSystem.id"
                                                                                                                    :oid="groupParameter2[column.columnId].oid"
                                                                                                                    :fctId="column.fctId"
                                                                                                                    :node_id="column.nodeId"
                                                                                                                    :active="isActiveAccordion()"
                                                                                                                    :condition="groupParameter2[column.columnId].condition"
                                                                                                                    :uneditable="
                                                                                                                        !tabEditable(layer.name) || groupParameter2[column.columnId].uneditable
                                                                                                                    "
                                                                                                                    :endpoint="groupParameter2[column.columnId].endpoint"
                                                                                                                    :type="groupParameter2[column.columnId].type"
                                                                                                                    :enum="groupParameter2[column.columnId].enum"
                                                                                                                    :mask="groupParameter2[column.columnId].mask"
                                                                                                                    :bit="groupParameter2[column.columnId].bit"
                                                                                                                    :reset="groupParameter2[column.columnId].reset"
                                                                                                                    :omit="groupParameter2[column.columnId].omit"
                                                                                                                    :restart="
                                                                                                                        groupParameter2[column.columnId].restart
                                                                                                                            ? groupParameter2[column.columnId].restart
                                                                                                                            : false
                                                                                                                    "
                                                                                                                    @newChild="registerNewChild($event, groupParameter2)"
                                                                                                                    @noChildFound="registerMissingChild($event, groupParameter2)"
                                                                                                                ></lazy-parameter>

                                                                                                                <span v-if="!groupParameter2[column.columnId]">n/a</span>
                                                                                                            </td>
                                                                                                        </template>

                                                                                                        <td v-if="!groupParameter2.group_name" class="table__data table__data--no-border-left"></td>
                                                                                                    </tr>
                                                                                                </template>
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </slide-down-up-transition>
                                                                            </td>

                                                                            <td v-if="!groupParameter.group_name" class="table__data table__data--label accordion__content__table__label">
                                                                                <span
                                                                                    v-if="layer.name == 'oem'"
                                                                                    v-html="highlightQuery($t('oids_oem.' + groupParameter[Object.keys(groupParameter)[0]].oid))"
                                                                                ></span>
                                                                                <span
                                                                                    v-else
                                                                                    v-html="
                                                                                        highlightQuery(
                                                                                            $t(
                                                                                                groupParameter[Object.keys(groupParameter)[0]].translation ||
                                                                                                    'oids.' + groupParameter[Object.keys(groupParameter)[0]].oid
                                                                                            )
                                                                                        )
                                                                                    "
                                                                                ></span>
                                                                            </td>

                                                                            <template v-for="(column, index) in tab.columns">
                                                                                <td v-if="!groupParameter.group_name" class="table__data table__data--parameter" :key="index">
                                                                                    <lazy-parameter
                                                                                        v-if="groupParameter[column.columnId]"
                                                                                        :id="activeSystem.id"
                                                                                        :oid="groupParameter[column.columnId].oid"
                                                                                        :fctId="column.fctId"
                                                                                        :node_id="column.nodeId"
                                                                                        :active="isActiveAccordion()"
                                                                                        :condition="groupParameter[column.columnId].condition"
                                                                                        :uneditable="!tabEditable(layer.name) || groupParameter[column.columnId].uneditable"
                                                                                        :endpoint="groupParameter[column.columnId].endpoint"
                                                                                        :type="groupParameter[column.columnId].type"
                                                                                        :enum="groupParameter[column.columnId].enum"
                                                                                        :mask="groupParameter[column.columnId].mask"
                                                                                        :bit="groupParameter[column.columnId].bit"
                                                                                        :reset="groupParameter[column.columnId].reset"
                                                                                        :omit="groupParameter[column.columnId].omit"
                                                                                        :restart="groupParameter[column.columnId].restart ? groupParameter[column.columnId].restart : false"
                                                                                        @newChild="registerNewChild($event, groupParameter)"
                                                                                        @noChildFound="registerMissingChild($event, groupParameter)"
                                                                                    ></lazy-parameter>

                                                                                    <span v-if="!groupParameter[column.columnId]">n/a</span>
                                                                                </td>
                                                                            </template>

                                                                            <td v-if="!groupParameter.group_name" class="table__data table__data--no-border-left"></td>
                                                                        </tr>
                                                                    </template>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </slide-down-up-transition>
                                            </td>
                                            <!-- parameter -->
                                            <td v-if="!row.group_name">
                                                <span v-if="layer.name == 'oem'" v-html="highlightQuery($t(row[Object.keys(row)[0]].translation || 'oids_oem.' + row[Object.keys(row)[0]].oid))"></span>
                                                <span v-else v-html="highlightQuery($t(row[Object.keys(row)[0]].translation || 'oids.' + row[Object.keys(row)[0]].oid))"></span>
                                            </td>

                                            <template v-for="(column, index) in tab.columns">
                                                <td :key="index" v-if="!row.group_name">
                                                    <lazy-parameter
                                                        v-if="row[column.columnId]"
                                                        :id="activeSystem.id"
                                                        :oid="row[column.columnId].oid"
                                                        :fctId="column.fctId"
                                                        :node_id="column.nodeId"
                                                        :active="isActiveLayer(tab.name, layer.name)"
                                                        :condition="row[column.columnId].condition"
                                                        :uneditable="!tabEditable(layer.name) || row[column.columnId].uneditable"
                                                        :endpoint="row[column.columnId].endpoint"
                                                        :type="row[column.columnId].type"
                                                        :enum="row[column.columnId].enum"
                                                        :mask="row[column.columnId].mask"
                                                        :bit="row[column.columnId].bit"
                                                        :reset="row[column.columnId].reset"
                                                        :omit="row[column.columnId].omit"
                                                        :restart="row[column.columnId].restart ? row[column.columnId].restart : false"
                                                        @newChild="registerNewChild($event, row)"
                                                        @noChildFound="registerMissingChild($event, row)"
                                                    ></lazy-parameter>

                                                    <span v-if="!row[column.columnId]">n/a</span>
                                                </td>
                                            </template>
                                            <td v-if="!row.group_name" class="table__data table__data--no-border-left"></td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import LazyParameter from '@/components/LazyParameter';
import EditableModuleName from '@/components/EditableModuleName';
import SlideDownUpTransition from '@/components/SlideDownUpTransition';

import { mapState } from 'vuex';
import _ from 'lodash';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';
import keyBy from 'lodash/keyBy';
import isFunction from 'lodash/isFunction';
import debounce from 'lodash/debounce';
import parameterLayer from '@/mixins/parameterLayer';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    components: {
        PageHeader,
        LazyParameter,
        EditableModuleName,
        SlideDownUpTransition,
        PageSubnavigation,
    },
    mixins: [parameterLayer, generalLinks],
    data() {
        return {
            boilerParametersSearchIndex: [],
            layerParametersSearchIndex: [],
            searchInput: '',
            parameterCount: {},

            structure: [],
            boilers: {},
            boilerTab: {},
            boilerParameters: {},
            tabs: [
                {
                    name: 'boiler',
                    activeLayer: 'info',
                    layers: [
                        {
                            name: 'info',
                            uneditable: true,
                            rows: [],
                        },
                        {
                            name: 'operate',
                            rows: [],
                        },
                        {
                            name: 'service',
                            rows: [],
                        },
                        {
                            name: 'oem',
                            rows: [],
                        },
                    ],
                },
                {
                    name: 'control',
                    activeLayer: 'info',
                    layers: [
                        {
                            name: 'info',
                            uneditable: true,
                            rows: [],
                        },
                        {
                            name: 'operate',
                            rows: [],
                        },
                        {
                            name: 'service',
                            rows: [],
                        },
                        {
                            name: 'oem',
                            rows: [],
                        },
                    ],
                },
                {
                    name: 'system',
                    activeLayer: 'info',
                    layers: [
                        {
                            name: 'info',
                            uneditable: true,
                            rows: [],
                        },
                        {
                            name: 'operate',
                            rows: [],
                        },
                        {
                            name: 'service',
                            rows: [],
                        },
                        {
                            name: 'oem',
                            rows: [],
                        },
                    ],
                },
            ],
            activeTab: false,
            tabFctTypes: {
                boiler: [7, 8, 9, 10, 25, 26],
                control: [14, 2, 1],
                system: [4, 5, 15, 16, 20],
            },
        };
    },
    computed: {
        ...mapState(['activeSystem']),
    },
    watch: {
        activeSystem(newVal) {
            if (newVal) {
                this.fetchStructure();
            }
            setTimeout(() => {
                this.initSliders();
            }, 100);
        },
        structure() {
            this.boilerTab = find(this.tabs, { name: 'boiler' });

            if (this.structure && this.structure.length > 0) {
                forEach(this.tabs, tab => {
                    tab.columns = _.chain(this.structure)
                        .map(mod => {
                            return map(mod.functions, f => {
                                return _.chain(mod)
                                    .omit(['functions'])
                                    .assign(f)
                                    .value();
                            });
                        })
                        .flatten()
                        .filter(mod => {
                            return this.tabFctTypes[tab.name].indexOf(mod.fctType) >= 0 && !mod.lock;
                        })
                        .map(column => {
                            column.columnId = column.nodeId + '/' + column.fctId;
                            return column;
                        })
                        .sortBy('nodeId')
                        .value();

                    // set boiler overview parameters
                    if (tab.name == 'boiler') {
                        let tempRows = [];

                        forEach(tab.columns, column => {
                            let parameters = this.parameterLayerGet('overview', column.fctType, this.getDeviceClass(column.programId));
                            parameters = this.addKeyToRows(parameters, column.columnId);
                            tempRows = tempRows.concat(parameters);
                        });

                        this.boilerParameters = this.groupRows(tempRows);
                    }

                    // get configured layer parameters an merge them for display
                    setTimeout(() => {
                        // timeout to let boilerParameters load first
                        forEach(tab.layers, layer => {
                            let tempRows = [];
                            forEach(tab.columns, column => {
                                let fetchedParameters = this.parameterLayerGet(layer.name, column.fctType, this.getDeviceClass(column.programId));
                                fetchedParameters = this.addKeyToRows(fetchedParameters, column.columnId);
                                tempRows = tempRows.concat(fetchedParameters);
                            });
                            layer.rows = this.groupRows(tempRows);
                        });
                    }, 1000);
                });
            }
        },
    },
    created() {
        this.resetParameterCount();
        this.fetchStructure();
        this.activeTab = this.tabs[0].name;
    },
    mounted() {
        if (this.activeSystem) {
            setTimeout(() => {
                this.initSliders();
            }, 100);
        }
    },
    methods: {
        initSliders() {
            const mainSliderEl = this.$refs['headerSlider'];
            const subSliderEls = this.$refs['subheaderSlider'];
            if (mainSliderEl) {
                const swiperParams = {
                    slidesPerView: 1.5,
                    breakpoints: {
                        480: {
                            slidesPerView: 3,
                        },
                    },
                };
                Object.assign(mainSliderEl, swiperParams);
                mainSliderEl.initialize();
            }
            if (subSliderEls && subSliderEls.length) {
                subSliderEls.forEach(sliderEl => {
                    const swiperParams = {
                        slidesPerView: 2.5,
                        breakpoints: {
                            480: {
                                slidesPerView: 4,
                            },
                        },
                    };
                    Object.assign(sliderEl, swiperParams);
                    sliderEl.initialize();
                });
            }
        },
        tabVisible(type, tab) {
            if (type === 'info') {
                return this.$can('see Infolayer', this.activeSystem);
            } else if (type === 'operate') {
                return this.$can('see Operatorlayer', this.activeSystem);
            } else if (type === 'service') {
                return this.$can('see Servicelayer', this.activeSystem);
            } else if (type === 'oem') {
                let oemLayer = find(tab.layers, { name: 'oem' });

                return this.$can('see OEMlayer', this.activeSystem) && oemLayer && oemLayer.rows && oemLayer.rows.length;
            }
            return false;
        },
        tabEditable(type) {
            if (type === 'info') {
                return false;
            } else if (type === 'operate') {
                return this.$can('edit Operatorlayer', this.activeSystem);
            } else if (type === 'service') {
                return this.$can('edit Servicelayer', this.activeSystem);
            } else if (type === 'oem') {
                return this.$can('edit OEMlayer', this.activeSystem);
            }
            return true;
        },
        fetchStructure() {
            if (this.activeSystem && this.activeSystem.id) {
                api.systems.structure({ id: this.activeSystem.id }).then(response => {
                    this.structure = response.data.structure;
                });
            }
        },
        reload() {
            this.structure = [];
            this.fetchStructure();
            this.activeTab = this.tabs[0].name;
        },
        getDeviceClass(programId) {
            return programId.substring(6, 10);
        },
        addKeyToRows(rows, columnId) {
            forEach(rows, row => {
                if (row.group_name) {
                    row.parameters = this.addKeyToRows(row.parameters, columnId);
                } else {
                    row.key = columnId;
                }
            });
            return rows;
        },
        groupRows(rows) {
            let tempRows = _.values(
                groupBy(rows, row => {
                    if (row.group_name) {
                        return row.group_name;
                    } else {
                        return row.oid;
                    }
                })
            );

            tempRows = map(tempRows, row => {
                if (row.length && row[0] && row[0].group_name) {
                    // handle group parameters
                    let fetchedParameters = reduce(
                        row,
                        (result, n) => {
                            result = result.concat(n.parameters);
                            return result;
                        },
                        []
                    );

                    row[0].parameters = this.groupRows(fetchedParameters);
                    return row[0];
                } else {
                    return keyBy(row, 'key');
                }
            });

            return tempRows;
        },
        hideRow(elem, i) {
            this.$set(elem[i], 'hidden', true);
        },
        switchTab(tabname) {
            this.activeTab = tabname;
        },
        switchLayer(tabname, layername) {
            find(this.tabs, { name: tabname }).activeLayer = layername;
        },
        isActiveLayer(tabname, layername) {
            return this.activeTab == tabname && find(this.tabs, tab => tab.name == tabname).activeLayer == layername;
        },
        checkIsFunction(f) {
            return isFunction(f);
        },
        isActiveAccordion() {
            return true;
        },
        toggleAccordionRow(row) {
            let truce = row.accordionOpen;
            if (truce !== undefined) {
                truce = !truce;
            } else {
                truce = true;
            }
            this.$set(row, 'accordionOpen', truce);
        },
        registerNewChild(childId, row) {
            if (row.children === undefined) this.$set(row, 'children', []);
            row.children.push(childId);
        },
        registerMissingChild(childId, row) {
            if (row.missingChildren === undefined) this.$set(row, 'missingChildren', []);
            row.missingChildren.push(childId);
        },
        compareChildren(row) {
            if (row.missingChildren === undefined) {
                return true;
            }
            if (row.missingChildren.length >= row.children.length) {
                return false;
            } else {
                return true;
            }
        },
        checkAccordionChildren(row) {
            if (!row.group_name) return true;

            let noChildren = 0;
            forEach(row.parameters, p => {
                if (p.missingChildren === undefined) return;
                if (p.missingChildren.length >= p.children.length) noChildren++;
            });
            if (noChildren >= 0 && noChildren >= row.parameters.length) {
                return false;
            } else {
                return true;
            }
        },
        resetParameterCount() {
            this.parameterCount = {
                boiler: {
                    count: 0,
                    info: 0,
                    operate: 0,
                    service: 0,
                    oem: 0,
                },
                control: {
                    count: 0,
                    info: 0,
                    operate: 0,
                    service: 0,
                },
                system: {
                    count: 0,
                    info: 0,
                    operate: 0,
                    service: 0,
                },
            };
        },
        searchHandler: debounce(function(e) {
            this.searchInput = e;

            if (e === '' || e.trim().length < 1) {
                this.boilerParametersSearchIndex = [];
            } else {
                this.resetParameterCount();
                this.filterSearch(e);
            }

            this.$forceUpdate();
        }, 300),
        filterSearch(query) {
            // filter lines in parameter table
            this.boilerParametersSearchIndex = [];
            this.boilerParameters.forEach(p => {
                if (
                    this.$t(p[Object.keys(p)[0]].translation || 'oids.' + p[Object.keys(p)[0]].oid)
                        .toLowerCase()
                        .indexOf(query.toLowerCase()) > -1
                ) {
                    this.boilerParametersSearchIndex.push({
                        key: p[Object.keys(p)[0]].key,
                        oid: p[Object.keys(p)[0]].oid,
                    });
                }
            });
            this.boilerParametersFiltered();

            // filter layered parameters
            this.layerParametersSearchIndex = [];
            this.tabs.forEach(t => {
                const topLayerName = t.name;
                t.layers.forEach(l => {
                    const subLayerName = l.name;

                    l.rows.forEach(r => {
                        // check parameters in groups
                        if (r.hasOwnProperty('group_name')) {
                            this.addGroupElement(r, topLayerName, subLayerName, query, []);

                            // check normal parameters
                        } else {
                            this.addLayerRowElement(r, topLayerName, subLayerName, [], query);
                        }
                    });
                });
            });

            this.tabs.forEach(t => {
                t.layers.forEach(l => {
                    this.filteredLayerRow(l.rows, t.name, l.name, false);

                    l.rows.forEach(r => {
                        if (r.hasOwnProperty('group_name')) {
                            this.filteredLayerRow(r.parameters, t.name, l.name, r.group_name);

                            r.parameters.forEach(gr => {
                                if (gr.hasOwnProperty('group_name')) {
                                    this.filteredLayerRow(gr.parameters, t.name, l.name, gr.group_name);
                                }
                            });
                        }
                    });
                });
            });
        },
        addGroupElement(r, toplayer, sublayer, query, parentGroup) {
            const groupNameKey = r.group_name;
            const groupname = this.$te('emStrIds.' + groupNameKey) ? this.$t('emStrIds.' + groupNameKey) : this.$t('systems.parameters.groups.' + groupNameKey);

            if (groupname.toLowerCase().indexOf(query) > -1) {
                this.layerParametersSearchIndex.push({
                    topLayerName: toplayer,
                    subLayerName: sublayer,
                    groupName: groupNameKey,
                    parentGroup: parentGroup,
                });
                this.parameterCount[toplayer].count++;
                this.parameterCount[toplayer][sublayer]++;
            }

            let passGroup = parentGroup;
            passGroup.push(groupNameKey);

            r.parameters.forEach(gr => {
                if (gr.hasOwnProperty('group_name')) {
                    this.addGroupElement(gr, toplayer, sublayer, query, [passGroup[0]]);
                } else {
                    this.addLayerRowElement(gr, toplayer, sublayer, passGroup, query);
                }
            });
        },
        addLayerRowElement(r, toplayer, sublayer, groupname, query) {
            if (
                (sublayer === 'oem' &&
                    this.$t(r[Object.keys(r)[0]].translation || 'oids_oem.' + r[Object.keys(r)[0]].oid)
                        .toLowerCase()
                        .indexOf(query.toLowerCase()) > -1) ||
                (sublayer !== 'oem' &&
                    this.$t(r[Object.keys(r)[0]].translation || 'oids.' + r[Object.keys(r)[0]].oid)
                        .toLowerCase()
                        .indexOf(query.toLowerCase()) > -1)
            ) {
                this.layerParametersSearchIndex.push({
                    topLayerName: toplayer,
                    subLayerName: sublayer,
                    parentGroup: groupname,
                    key: r[Object.keys(r)[0]].key,
                    oid: r[Object.keys(r)[0]].oid,
                });
                this.parameterCount[toplayer].count++;
                this.parameterCount[toplayer][sublayer]++;
            }
        },
        filteredLayerRow(row, toplayer, sublayer, groupname) {
            let filteredData = [];
            if (this.searchInput.length > 0) {
                row.forEach(p => {
                    if (p.hasOwnProperty('group_name')) {
                        if (
                            this.layerParametersSearchIndex.some(i => i.topLayerName == toplayer && i.subLayerName == sublayer && i.groupName == p.group_name) ||
                            this.layerParametersSearchIndex.some(i => i.topLayerName == toplayer && i.subLayerName == sublayer && i.parentGroup.indexOf(p.group_name) >= 0)
                        ) {
                            p.filtered = false;
                        } else {
                            p.filtered = true;
                        }
                    } else {
                        const findObject = {
                            key: p[Object.keys(p)[0]].key,
                            oid: p[Object.keys(p)[0]].oid,
                        };

                        if (
                            this.layerParametersSearchIndex.some(
                                i =>
                                    (i.key =
                                        findObject.key &&
                                        i.oid == findObject.oid &&
                                        i.topLayerName == toplayer &&
                                        i.subLayerName == sublayer &&
                                        (i.parentGroup.indexOf(groupname) >= 0 || (groupname === false && i.parentGroup.length === 0)))
                            )
                        ) {
                            p.filtered = false;
                        } else {
                            p.filtered = true;
                        }
                    }
                });
            } else {
                filteredData = row;
            }
            return filteredData;
        },
        highlightQuery(value) {
            const initial = value.replace(/(<([^>]+)>)/gi, '');
            const reg = new RegExp(this.searchInput, 'gi');
            const result = initial.replace(reg, function(str) {
                return '<span class="parametersearchhighlight">' + str + '</span>';
            });
            return result;
        },
        boilerParametersFiltered() {
            if (this.searchInput.length > 0) {
                this.boilerParameters.forEach(p => {
                    const findObject = {
                        key: p[Object.keys(p)[0]].key,
                        oid: p[Object.keys(p)[0]].oid,
                    };
                    if (this.boilerParametersSearchIndex.some(i => (i.key = findObject.key && i.oid == findObject.oid))) {
                        p.filtered = false;
                    } else {
                        p.filtered = true;
                    }
                });
            }
        },
    },
};
</script>
